import React from 'react';
import { motion } from 'framer-motion';
import { FiRefreshCw, FiArrowLeft, FiShield, FiBell } from 'react-icons/fi';

interface AdminHeaderProps {
  userEmail: string;
  userRole: string;
  onRefresh: () => void;
  loading?: boolean;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ 
  userEmail, 
  userRole, 
  onRefresh, 
  loading 
}) => {
  return (
    <header className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur-xl border-b border-slate-700/50">
      <div className="px-6 py-4 flex items-center justify-between">
        {/* Left: Breadcrumb */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-slate-500">Admin</span>
            <span className="text-slate-600">/</span>
            <span className="text-white font-medium">Dashboard</span>
          </div>
        </div>

        {/* Right: Actions & User */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
          >
            <FiBell />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full" />
          </motion.button>

          {/* Refresh */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onRefresh}
            disabled={loading}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
          </motion.button>

          {/* Divider */}
          <div className="h-6 w-px bg-slate-700" />

          {/* User Info */}
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <p className="text-sm font-medium text-white truncate max-w-[160px]">{userEmail}</p>
              <div className="flex items-center justify-end space-x-1">
                <FiShield className="text-cyan-400 text-xs" />
                <span className="text-xs text-cyan-400 capitalize">{userRole}</span>
              </div>
            </div>
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white font-semibold text-sm">
              {userEmail?.charAt(0).toUpperCase()}
            </div>
          </div>

          {/* Back to user app — used to be a red logout-looking icon
              which nobody could tell went back to /dashboard. Now
              labelled and colored cyan so it reads as navigation, not
              a destructive action. */}
          <motion.a
            href="/dashboard"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            title="Return to user dashboard"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:border-cyan-400/60 transition-colors text-sm font-medium"
          >
            <FiArrowLeft />
            <span className="hidden sm:inline">Back to app</span>
          </motion.a>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;